import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';

export default function CookiePolicy() {
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-12">
        <h1 className="font-heading text-3xl sm:text-4xl font-medium text-[#1A1A1A] mb-2" data-testid="cookies-title">Cookie Policy</h1>
        <p className="text-sm text-stone-400 mb-8">Last updated: February 2026</p>

        <div className="prose prose-stone max-w-none space-y-6 text-sm leading-relaxed text-stone-600">
          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">1. About Cookies</h2>
            <p>
              Cookies are small text files a site stores on your device to remember things between visits. Some keep you
              signed in, others remember which provider or referral code brought you to a page so the right attribution
              is applied at checkout. This policy is part of our
              <Link to="/privacy" className="text-[#D4AF37] underline"> Privacy Policy</Link> and sets out every cookie
              Zuriya Ltd uses.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">2. Cookies We Use</h2>
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-sm border border-[#E6E4DD] rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-[#F9F6F0]">
                    <th className="text-left py-2 px-3 font-medium text-[#1A1A1A]">Cookie</th>
                    <th className="text-left py-2 px-3 font-medium text-[#1A1A1A]">Set by</th>
                    <th className="text-left py-2 px-3 font-medium text-[#1A1A1A]">Type</th>
                    <th className="text-left py-2 px-3 font-medium text-[#1A1A1A]">Purpose</th>
                    <th className="text-left py-2 px-3 font-medium text-[#1A1A1A]">Duration</th>
                  </tr>
                </thead>
                <tbody className="align-top">
                  <tr className="border-t border-[#E6E4DD]">
                    <td className="py-2 px-3 font-mono text-xs">session_token</td>
                    <td className="py-2 px-3">Zuriya (first-party)</td>
                    <td className="py-2 px-3">Strictly necessary</td>
                    <td className="py-2 px-3">Keeps you signed in. Stored as an httpOnly, Secure, SameSite cookie so it cannot be read by JavaScript.</td>
                    <td className="py-2 px-3">7 days (1 day for guest sessions)</td>
                  </tr>
                  <tr className="border-t border-[#E6E4DD]">
                    <td className="py-2 px-3 font-mono text-xs">zuriya_source</td>
                    <td className="py-2 px-3">Zuriya (first-party)</td>
                    <td className="py-2 px-3">Functional</td>
                    <td className="py-2 px-3">Records whether you arrived via a provider&apos;s direct-share link (<code>direct_link</code>) so the platform can apply the correct commission to any booking you make with that provider (0% vs marketplace rate).</td>
                    <td className="py-2 px-3">30 days</td>
                  </tr>
                  <tr className="border-t border-[#E6E4DD]">
                    <td className="py-2 px-3 font-mono text-xs">zuriya_source_provider</td>
                    <td className="py-2 px-3">Zuriya (first-party)</td>
                    <td className="py-2 px-3">Functional</td>
                    <td className="py-2 px-3">Stores the username of the provider whose share link you followed, used together with <code>zuriya_source</code>.</td>
                    <td className="py-2 px-3">30 days</td>
                  </tr>
                  <tr className="border-t border-[#E6E4DD]">
                    <td className="py-2 px-3 font-mono text-xs">zuriya_referral</td>
                    <td className="py-2 px-3">Zuriya (first-party)</td>
                    <td className="py-2 px-3">Functional</td>
                    <td className="py-2 px-3">Holds a friend&apos;s referral code if you arrived via a <code>/r/&#123;code&#125;</code> landing page. On your first qualifying booking the platform applies a £5 discount and credits the referrer.</td>
                    <td className="py-2 px-3">30 days</td>
                  </tr>
                  <tr className="border-t border-[#E6E4DD]">
                    <td className="py-2 px-3 font-mono text-xs">stripe_* / __stripe_*</td>
                    <td className="py-2 px-3">Stripe (third-party)</td>
                    <td className="py-2 px-3">Strictly necessary</td>
                    <td className="py-2 px-3">Used during checkout and for fraud-prevention by Stripe Payments UK, Ltd. See <a href="https://stripe.com/gb/privacy" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] underline">Stripe&apos;s privacy policy</a>.</td>
                    <td className="py-2 px-3">Up to 1 year (varies by cookie)</td>
                  </tr>
                  <tr className="border-t border-[#E6E4DD]">
                    <td className="py-2 px-3 font-mono text-xs">__cf_bm / cf_clearance</td>
                    <td className="py-2 px-3">Cloudflare (third-party)</td>
                    <td className="py-2 px-3">Strictly necessary</td>
                    <td className="py-2 px-3">Bot mitigation and edge-security, protects the platform from automated attacks. See <a href="https://www.cloudflare.com/en-gb/privacypolicy/" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] underline">Cloudflare&apos;s privacy policy</a>.</td>
                    <td className="py-2 px-3">30 minutes to 30 days</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-stone-500">
              Zuriya does not set any advertising, cross-site-tracking, or analytics cookies.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">3. Categories Explained</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Strictly necessary</strong> cookies are required for the platform to operate, for example to keep you signed in, to let Stripe process your payment, and to let Cloudflare block malicious traffic. These cannot be disabled without breaking core features.</li>
              <li><strong>Functional</strong> cookies remember attribution and referral context so you receive the right discount and so providers are credited correctly for bookings they refer. You can clear them at any time from your browser without affecting sign-in; doing so just means subsequent bookings will be treated as marketplace bookings with no referral applied.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">4. What We Don&apos;t Use</h2>
            <p>Zuriya does not use:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Google Analytics, Meta Pixel, TikTok Pixel, or any other behavioural-tracking pixel.</li>
              <li>Advertising or remarketing cookies.</li>
              <li>Cross-site user-profiling or audience-building cookies.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">5. Managing Cookies</h2>
            <p>
              You can delete Zuriya&apos;s own cookies at any time through your browser settings (typically under
              &quot;Privacy&quot; or &quot;Site data&quot;). Blocking the <code>session_token</code> cookie will prevent
              you from signing in; blocking Stripe cookies will prevent checkout. Blocking the attribution cookies
              (<code>zuriya_source</code>, <code>zuriya_source_provider</code>, <code>zuriya_referral</code>) is safe and
              just removes any tracking benefit for the referrer.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">6. Changes to This Policy</h2>
            <p>
              If we add a new cookie or change how an existing one is used we will update this page and the
              &quot;Last updated&quot; date above. Material changes are also flagged in-app.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">7. Contact</h2>
            <p>
              For questions about our cookies, email
              <a href="mailto:privacy@zuriya.co.uk" className="text-[#D4AF37] underline"> privacy@zuriya.co.uk</a>. For a
              full picture of how we handle your personal data, see our
              <Link to="/privacy" className="text-[#D4AF37] underline"> Privacy Policy</Link>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
